'use client'

import { FormButton, FormGroup, FormInput } from '@/components/form'
import { authActions } from '@/lib/actions/auth'
import { track } from '@/lib/tracking'
import { Turnstile } from '@marsidev/react-turnstile'
import { Field, Form, Formik } from 'formik'
import { useRouter } from 'next/navigation'
import { useRef, useState } from 'react'
import * as Yup from 'yup'

const WEB_URL = process.env.NEXT_PUBLIC_SITE_URL
// Use Cloudflare's test key for localhost, production key otherwise
const TURNSTILE_SITE_KEY =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? '1x00000000000000000000AA' // Cloudflare test key (always passes)
    : '0x4AAAAAACSR-BU2Cm_M_rSH'

// Define validation schema with Yup
const validationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
})

const INITIAL = { email: '', website: '' }

// Off-screen, not display:none, and named like a real field, so bots fill
// it in and browsers don't autofill it.
const Honeypot = () => (
  <div
    aria-hidden="true"
    className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden"
  >
    <label>
      Website
      <Field name="website" type="text" tabIndex={-1} autoComplete="off" />
    </label>
  </div>
)

const SignUpForm = ({
  buttonLabel = 'Sign Up',
  className = '',
  onSuccess,
  mode = 'signup', // 'signup' or 'login'
  trackingLocation = 'unknown',
  inline = false, // compact email + button row (e.g. header)
  // The subscribe card's row: fills its container, shows errors under it.
  card = false,
  // Where to land after the code is entered, a path on this site (the MCP
  // consent page, 2026-10-27); /welcome or /account otherwise.
  next = null,
}) => {
  const [captchaToken, setCaptchaToken] = useState()
  const [formStatus, setFormStatus] = useState(null)
  const turnstileRef = useRef(null)
  const router = useRouter()

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    // Honeypot: a field people never see. Bots that fill every input fill
    // this one, and the signup is quietly dropped (Sam, 2026-09-30; junk
    // addresses had got through before). Turnstile stays as the first layer.
    if (values.website) {
      resetForm()
      setSubmitting(false)
      return
    }
    try {
      // Email-redirect fallback (used only if the email also carries a magic
      // link). The primary flow is the 6-digit code entered on /confirm-email.
      const redirectUrl =
        mode === 'signup'
          ? `${WEB_URL}/auth/callback?next=/welcome&intent=subscribe`
          : `${WEB_URL}/auth/callback?next=/account`

      // Send the OTP email (6-digit code)
      const { error } = await authActions.signInWithOtp(
        values.email,
        redirectUrl,
        { captchaToken: captchaToken, createUser: mode === 'signup' }
      )

      if (error) {
        throw error
      }

      // ✅ Track: signup_initiated or login_initiated, with where the form
      // was (`location`, the registered GA4 dimension; it was
      // tracking_location until 2026-10-25, which GA4 never registered)
      track(mode === 'signup' ? 'signup_initiated' : 'login_initiated', {
        email_domain: values.email.split('@')[1],
        location: trackingLocation,
      })

      if (onSuccess) {
        onSuccess()
      }

      resetForm()

      // Send them to the code-entry screen to verify
      router.push(
        `/confirm-email?email=${encodeURIComponent(values.email)}&mode=${mode}${
          next ? `&next=${encodeURIComponent(next)}` : ''
        }`
      )
    } catch (error) {
      console.error('Auth error:', error)

      let errorType = 'unknown'
      if (error.message.includes('Email rate limit exceeded')) {
        errorType = 'rate_limit'
        setFormStatus(
          'Too many requests. Please wait a moment before trying again.'
        )
      } else if (error.message.includes('Invalid email')) {
        errorType = 'invalid_email'
        setFormStatus('Please enter a valid email address.')
      } else if (/signups not allowed/i.test(error.message)) {
        // Login with an address that has no account (login can't create one).
        errorType = 'no_account'
        setFormStatus(
          "We couldn't find an account for that email. Subscribe to get started."
        )
      } else if (error.message.includes('Captcha verification failed')) {
        errorType = 'captcha_failed'
        setFormStatus('Captcha verification failed. Please try again.')
      } else {
        setFormStatus('Something went wrong. Please try again.')
      }

      track(mode === 'signup' ? 'signup_failed' : 'login_failed', {
        error_type: errorType,
        location: trackingLocation,
      })

      // Clear error message after 5 seconds
      setTimeout(() => setFormStatus(null), 5000)

      // Reset captcha for retry
      setCaptchaToken(undefined)
      turnstileRef.current?.reset()
    } finally {
      setSubmitting(false)
    }
  }

  if (card) {
    return (
      <div className={className}>
        <Formik
          initialValues={INITIAL}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="relative">
              <div className="flex gap-2">
                <Field
                  name="email"
                  type="email"
                  placeholder="Your email"
                  autoComplete="email"
                  required
                  aria-label="Email address"
                  className="h-10 min-w-0 flex-1 rounded-lg border border-foreground/40 bg-white px-3 font-sans text-sm text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none dark:bg-background"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-10 shrink-0 rounded-lg bg-foreground px-4 font-sans text-sm font-semibold text-background transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {isSubmitting ? '…' : buttonLabel}
                </button>
              </div>
              <Honeypot />
              {(formStatus || (touched.email && errors.email)) && (
                <p className="mt-2 font-sans text-xs text-destructive">
                  {formStatus || errors.email}
                </p>
              )}
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                options={{ size: 'invisible' }}
                onSuccess={(token) => setCaptchaToken(token)}
                onExpire={() => {
                  setCaptchaToken(undefined)
                  turnstileRef.current?.reset()
                }}
              />
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  // Compact inline variant (email + button on one row) for tight spaces
  if (inline) {
    return (
      <div className={className}>
        <Formik
          initialValues={INITIAL}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="flex items-center gap-2">
              <Field
                name="email"
                type="email"
                placeholder="Your email"
                autoComplete="email"
                required
                aria-label="Email address"
                className="h-9 w-40 lg:w-52 rounded-lg border border-foreground/40 bg-white px-3 font-sans text-sm text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none dark:bg-background"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="h-9 shrink-0 rounded-lg bg-foreground px-4 font-sans text-sm font-semibold text-background transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {isSubmitting ? '…' : buttonLabel}
              </button>
              <Honeypot />
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                options={{ size: 'invisible' }}
                onSuccess={(token) => setCaptchaToken(token)}
                onExpire={() => {
                  setCaptchaToken(undefined)
                  turnstileRef.current?.reset()
                }}
              />
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  return (
    <div className={className}>
      {formStatus && (
        <div className="mb-4 p-4 rounded-md text-center bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
          {formStatus}
        </div>
      )}

      <Formik
        initialValues={INITIAL}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <FormGroup>
              <FormInput
                name="email"
                type="email"
                placeholder="Enter your email"
                autoComplete="email"
                aria-label="Email address"
                showLabel={false}
                required
              />

              <FormButton
                type="submit"
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Sending...' : buttonLabel}
              </FormButton>
              <Honeypot />

              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                options={{ size: 'invisible' }}
                onSuccess={(token) => {
                  setCaptchaToken(token)
                }}
                onExpire={() => {
                  setCaptchaToken(undefined)
                  turnstileRef.current?.reset()
                }}
              />
            </FormGroup>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default SignUpForm
